// ─── API layer for KPI due dates ─────────────────────────────────────────────
// All endpoint knowledge for this feature lives here; components call these
// named functions and never assemble URLs. apiCall injects auth and returns
// { isSuccess, data, message }.
import { apiCall } from "../../../_services/apiCall";
import config from "../../../config/config";

const DUE_DATES = `${config.POSTLOGIN_API_URL_COMPANY}due-dates`;

/**
 * Lists the frameworks the company reports against.
 * @param {number} userId the signed-in user.
 * @returns {Promise<object>} apiCall result whose data is the framework array.
 */
export const listFrameworks = (userId) =>
  apiCall(
    `${config.POSTLOGIN_API_URL_COMPANY}getFramework`,
    {},
    { type: "ALL", userId },
    "GET",
  );

/**
 * Lists the quantitative KPIs a deadline can be set against.
 *
 * This is the same call Data Management makes, deliberately: a due date only
 * means something for a KPI that receives data there, so the two screens must
 * list the same set. The KPI library endpoint used by Settings → Unit returns
 * every question in the catalogue, most of which are never reported against.
 * @param {number} financialYearId the financial year.
 * @param {number[]} frameworkIds the company's frameworks.
 * @returns {Promise<object>} apiCall result whose data.data is the KPI array.
 */
export const listQuantitativeKpis = (financialYearId, frameworkIds) =>
  apiCall(
    `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestionnew`,
    {},
    {
      financialYearId,
      frameworkIds,
      question_type_filter: JSON.stringify(["quantitative"]),
    },
    "GET",
  );

/**
 * Lists the deadlines configured for a financial year.
 * @param {number} financialYearId the financial year.
 * @returns {Promise<object>} apiCall result whose data is the due-date array.
 */
export const listDueDates = (financialYearId) =>
  apiCall(DUE_DATES, {}, { financialYearId }, "GET");

/**
 * Sets one deadline, at module, location or KPI scope.
 * @param {object} payload the scope and date.
 * @returns {Promise<object>} apiCall result whose data is the stored due date.
 */
export const setDueDate = (payload) => apiCall(DUE_DATES, {}, payload, "POST");

/**
 * Sets many deadlines in one request — Bulk Apply.
 *
 * Returns partial success: `data.saved` are stored, `data.skipped` carries the
 * rows that failed validation and why, so one bad row doesn't lose the batch.
 * @param {object[]} dueDates the deadlines to store.
 * @returns {Promise<object>} apiCall result with `saved` and `skipped`.
 */
export const setDueDatesBulk = (dueDates) =>
  apiCall(`${DUE_DATES}/bulk`, {}, { dueDates }, "POST");

/**
 * Clears one deadline, reverting its KPIs to the next scope up.
 * @param {number} id the due date to clear.
 * @returns {Promise<object>} apiCall result.
 */
export const clearDueDate = (id) =>
  apiCall(`${DUE_DATES}/${id}`, {}, {}, "DELETE");

/**
 * Rebuilds the Query Builder's dataset records so a due-date change made here
 * shows up in the Query Builder and reminders immediately, instead of waiting
 * for the hourly sync. The 6th arg suppresses the global success alert.
 * @returns {Promise<object>} apiCall result.
 */
export const resyncDatasets = () =>
  apiCall(
    `${config.POSTLOGIN_API_URL_COMPANY}query-builder/resync`,
    {},
    {},
    "POST",
    true,
    false,
  );
